import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { User, Edit, Save, X, Camera, Lock, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

interface ProfileFormData {
  fullName: string;
  bio: string;
  profilePhotoUrl: string;
}

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<ProfileFormData>();

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    watch,
    formState: { errors: passwordErrors },
  } = useForm<ChangePasswordFormData>();

  const newPassword = watch('newPassword');

  useEffect(() => {
    if (user) {
      reset({
        fullName: user.fullName || '',
        bio: user.bio || '',
        profilePhotoUrl: user.profilePhotoUrl || '',
      });
    }
  }, [user, reset]);

  // Cleanup object URLs when selectedFile changes or component unmounts
  useEffect(() => {
    return () => {
      // Cleanup function to revoke object URLs
      if (selectedFile) {
        URL.revokeObjectURL(URL.createObjectURL(selectedFile));
      }
    };
  }, [selectedFile]);

  // Debug: Log user data when it changes
  useEffect(() => {
    if (user) {
      console.log('Profile page - User data updated:', {
        id: user.id,
        username: user.username,
        fullName: user.fullName,
        bio: user.bio,
        profilePhotoUrl: user.profilePhotoUrl,
        credits: user.credits
      });
    }
  }, [user]);

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    try {
      // Upload file if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const uploadResponse = await apiService.uploadFile(formData);
        
        // The backend returns the full URL, so we can use it directly
        data.profilePhotoUrl = uploadResponse.data;
      }

      await apiService.updateUserProfile(data);
      
      // Refresh user data to get the updated profile
      const updatedUserResponse = await apiService.getUserProfile();
      console.log('Profile update - Fresh user data from API:', updatedUserResponse.data);
      updateUser(updatedUserResponse.data);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      setSelectedFile(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitPassword = async (data: ChangePasswordFormData) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);
    try {
      await apiService.changePassword(data.currentPassword, data.newPassword);
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      resetPassword();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Profile...</h2>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="max-w-4xl mx-auto space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <motion.div 
        className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold gradient-text mb-2">Profile</h1>
            <p className="text-gray-600 text-lg">Welcome back, {user.fullName || user.username}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className={`flex items-center px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                isEditing 
                  ? 'border-2 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 bg-red-50' 
                  : 'border-2 border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400 bg-blue-50'
              }`}
            >
              {isEditing ? <X className="h-4 w-4 mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
            <button
              onClick={async () => {
                try {
                  console.log('Manually refreshing user profile...');
                  const response = await apiService.getUserProfile();
                  console.log('Manual refresh - User data from API:', response.data);
                  updateUser(response.data);
                  toast.success('Profile refreshed!');
                } catch (error) {
                  console.error('Failed to refresh profile:', error);
                  toast.error('Failed to refresh profile');
                }
              }}
              className="flex items-center px-4 py-3 rounded-xl text-sm font-medium border-2 border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400 bg-green-50 transition-all duration-300 transform hover:scale-105"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </motion.div>

      {/* Profile Form */}
      <motion.div 
        className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Profile Photo */}
          <div className="flex items-start space-x-8">
            <div className="relative group">
              <div className="h-32 w-32 rounded-full bg-gradient-to-r from-blue-100 to-teal-100 flex items-center justify-center overflow-hidden shadow-2xl group-hover:shadow-3xl transition-all duration-500 transform group-hover:scale-105">
                {/* Show selected file preview if editing */}
                {isEditing && selectedFile ? (
                  <img
                    src={URL.createObjectURL(selectedFile)}
                    alt="Profile preview"
                    className="h-32 w-32 rounded-full object-cover"
                  />
                ) : user.profilePhotoUrl ? (
                  <img
                    src={user.profilePhotoUrl}
                    alt="Profile"
                    className="h-32 w-32 rounded-full object-cover"
                    onError={(e) => {
                      // Hide the broken image and show fallback
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                {/* Fallback icon - hidden if image loads successfully */}
                <div className={`h-32 w-32 rounded-full bg-gradient-to-r from-blue-100 to-teal-100 flex items-center justify-center ${
                  (isEditing && selectedFile) || user.profilePhotoUrl ? 'hidden' : ''
                }`}>
                  <span className="text-4xl font-bold text-blue-600">
                    {user.username.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              {isEditing && (
                <label className="absolute bottom-2 right-2 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full p-3 cursor-pointer hover:from-blue-700 hover:to-teal-700 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110">
                  <Camera className="h-5 w-5 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-1">
                  {user.fullName || user.username}
                </h2>
                <p className="text-xl text-gray-500">
                  @{user.username}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-r from-blue-50 to-teal-50 px-4 py-2 rounded-full border border-blue-200">
                  <span className="text-lg font-semibold text-blue-700">{user.credits} Credits</span>
                </div>
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2 rounded-full border border-green-200">
                  <span className="text-lg font-semibold text-green-700">Active</span>
                </div>
              </div>
            </div>
          </div>

          {/* Full Name */}
          <div className="group">
            <label htmlFor="fullName" className="block text-lg font-semibold text-gray-700 mb-3">
              Full Name
            </label>
            <input
              {...register('fullName')}
              type="text"
              disabled={!isEditing}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02] group-hover:shadow-md"
              placeholder="Enter your full name"
            />
          </div>

          {/* Bio */}
          <div className="group">
            <label htmlFor="bio" className="block text-lg font-semibold text-gray-700 mb-3">
              Bio
            </label>
            <textarea
              {...register('bio')}
              rows={4}
              disabled={!isEditing}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02] group-hover:shadow-md resize-none"
              placeholder="Tell us about yourself..."
            />
          </div>

          {/* Email */}
          <div className="group">
            <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-3">
              Email
            </label>
            <input
              type="email"
              value={user.email}
              disabled
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 text-gray-500 border-blue-200"
            />
          </div>

          {/* Username */}
          <div className="group">
            <label htmlFor="username" className="block text-lg font-semibold text-gray-700 mb-3">
              Username
            </label>
            <input
              type="text"
              value={user.username}
              disabled
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 text-gray-500 border-blue-200"
            />
          </div>

          {/* Credits */}
          <div className="group">
            <label htmlFor="credits" className="block text-lg font-semibold text-gray-700 mb-3">
              Credits
            </label>
            <input
              type="text"
              value={user.credits}
              disabled
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 text-gray-500 border-blue-200"
            />
          </div>

          {/* Form Buttons */}
          {isEditing && (
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-blue-100">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setSelectedFile(null);
                  reset();
                }}
                className="flex items-center px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 transform hover:scale-105 font-medium"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-semibold"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Profile
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </motion.div>

      {/* Change Password Section */}
      <motion.div 
        className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Security Settings</h3>
            <p className="text-gray-600">Change your password to keep your account secure</p>
          </div>
          <button
            onClick={() => setShowChangePassword(!showChangePassword)}
            className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium"
          >
            {showChangePassword ? <X className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
            {showChangePassword ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showChangePassword && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-orange-100 pt-6"
          >
            <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-6">
              {/* Current Password */}
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-3">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    {...registerPassword('currentPassword')}
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 hover:border-orange-300 focus:scale-[1.02]"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-3">
                  New Password
                </label>
                <div className="relative">
                  <input
                    {...registerPassword('newPassword')}
                    type={showNewPassword ? 'text' : 'password'}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 hover:border-orange-300 focus:scale-[1.02]"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-3">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    {...registerPassword('confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 hover:border-orange-300 focus:scale-[1.02]"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <p className="text-red-600 text-sm mt-2">
                    {passwordErrors.confirmPassword.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="flex items-center px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-semibold"
                >
                  {isChangingPassword ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Changing...</span>
                    </div>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </motion.div>

      {/* Account Stats */}
      <motion.div 
        className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="mb-6">
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">Account Overview</h3>
          <p className="text-gray-600">Your learning journey and account status</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="group p-6 bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl border border-blue-200 hover:shadow-md transition-all duration-300 transform hover:scale-105">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600 mb-2">{user.credits}</p>
              <p className="text-lg text-gray-700 font-medium">Available Credits</p>
              <p className="text-sm text-blue-600 mt-2">Book learning sessions</p>
            </div>
          </div>
          
          <div className="group p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 hover:shadow-md transition-all duration-300 transform hover:scale-105">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600 mb-2">Active</p>
              <p className="text-lg text-gray-700 font-medium">Account Status</p>
              <p className="text-sm text-green-600 mt-2">Fully verified</p>
            </div>
          </div>
          
          <div className="group p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 hover:shadow-md transition-all duration-300 transform hover:scale-105">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600 mb-2">Member</p>
              <p className="text-lg text-gray-700 font-medium">Account Type</p>
              <p className="text-sm text-purple-600 mt-2">Standard benefits</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Profile; 